import { useCallback, useMemo, useState } from 'react'
import { knowledgeApi, type LeastSureItem, type TimelineItem, type TimelineStatus } from '../../services/api'
import {
  Button,
  Card,
  CardHeader,
  Chip,
  ChipGroup,
  EmptyState,
  EntryList,
  EntryRow,
  ErrorState,
  Hint,
  LoadMore,
  LoadingState,
  Meta,
  Meter,
  Stack,
} from '../../ds'
import { FromLine, leastSureName } from './LeastSure'
import { foldSameBelief, confidenceRange, sharedEvidenceLine, utcDay } from './leastSureFold'
import { scopeKey, usePagedRead } from './usePagedRead'

/**
 * "What she learned" — every belief she has formed here, newest first (MAR-389; the canvas's
 * Knowledge-2, be4/be5). A Knowledge place of its own (`?view=learned`).
 *
 * ── What the order and the filters claim ────────────────────────────────────────────────
 *
 * ⚠️ The server orders by when she FORMED each belief (`generatedAt`). "You confirmed" lists
 * confirmed beliefs in that order, not in the order they were answered — the index has no
 * settled-at order — and the copy says so rather than calling it "recent verdicts".
 *
 * ⚠️ "Expired" lists only expired beliefs still stored: a superseded belief is deleted 90 days
 * after she formed it. The filter says that too, so a short list is not read as "she rarely
 * lets one lapse".
 *
 * ⚠️ One belief is many rows. Every synthesis run writes a fresh row for each belief it still
 * holds and expires the last one, even from identical evidence — and a belief re-formed from
 * new evidence in the same words is the same belief too. The server lists such a
 * belief ONCE, at its newest row, and marks it `lineage: 'again'` ("Formed again") — so an
 * unchanged belief never reads as a new belief plus an expiry. A row the server could not place
 * in its chain carries no `lineage`, and says so rather than claiming "new" or "lapsed".
 *
 * ── The filter is in the URL ────────────────────────────────────────────────────────────
 *
 * `?view=learned&learned=confirmed` — so "You confirmed" is a link. `resolveLearnedFilter` /
 * `writeLearnedFilter` are the one reading and writing of it; the page owns the URL.
 *
 * ── Paging ──────────────────────────────────────────────────────────────────────────────
 *
 * `usePagedRead`, keyed on (workspace, filter). A page can be short — even empty — and still
 * have a cursor (the server reads a bounded number of index pages per call), so "that is
 * everything" is said only when the server returns no cursor.
 *
 * The same belief, in the same status, formed about several DISTINCT customers on one day folds
 * into one line (`foldSameBelief`, exact wording — the canvas's be7), opening to its members.
 *
 * ── The v6 anatomy (MAR-401, the canvas's Knowledge-2) ──────────────────────────────────
 *
 * One list on a dot rail (`EntryList rail`), the day in the left gutter on its first row. A
 * row's "From …" is the least-sure row's own (`FromLine`): it links the evidence item to its
 * Ledger receipt when the server attached one (the timeline links evidence like least-sure
 * does, MAR-390) and is plain text otherwise. A folded line prints a "From …" only when every
 * member's reads the same. Under the list, "Where it comes from" and the one way into the raw
 * stream ("Raw events", `?view=feed`). ⚠️ "Still open" stays, not the canvas's "New beliefs":
 * the filter is every unanswered belief, formed again or not, so "new" would overclaim.
 */

export type LearnedFilter = 'all' | TimelineStatus

const FILTERS: { key: LearnedFilter; label: string }[] = [
  { key: 'all', label: 'Everything' },
  { key: 'active', label: 'Still open' },
  { key: 'confirmed', label: 'You confirmed' },
  { key: 'refuted', label: 'You corrected' },
  { key: 'expired', label: 'Expired' },
]

/** The URL parameter that carries the filter, beside `view=learned`. */
export const LEARNED_PARAM = 'learned'

/** The filter a URL names — anything unknown or absent is `all`. */
export function resolveLearnedFilter(params: URLSearchParams): LearnedFilter {
  const v = params.get(LEARNED_PARAM)
  return FILTERS.some((f) => f.key === v) ? (v as LearnedFilter) : 'all'
}

/** The URL with this filter, every other parameter kept. `all` is the absence of one. */
export function writeLearnedFilter(current: URLSearchParams, filter: LearnedFilter): URLSearchParams {
  const next = new URLSearchParams(current)
  if (filter === 'all') next.delete(LEARNED_PARAM)
  else next.set(LEARNED_PARAM, filter)
  return next
}

/** What each filter's list is, said under it — including what it cannot show. */
const FILTER_NOTE: Record<LearnedFilter, string> = {
  all: 'Every belief she has formed, newest first. One she formed again — from the same evidence, or in the same words from new evidence — is listed once, where she last formed it.',
  active: 'Beliefs she holds and nobody has answered yet, newest first.',
  confirmed: 'Beliefs you confirmed, in the order she formed them — not the order you answered.',
  refuted: 'Beliefs you marked wrong, in the order she formed them — not the order you answered.',
  expired:
    'Beliefs she no longer holds, in the order she formed them. One she formed again — from the same evidence, or in the same words from new evidence — is not listed here; it shows where she formed it again. Expired beliefs are deleted 90 days after she formed them, so only those still held are listed.',
}

/**
 * The line above the belief: what happened, and about whom. An open row says "Formed again"
 * only when the server found an older row of the same belief; a row it could not place says
 * neither "new" nor "lapsed".
 */
function headOf(item: LearnedItem): string {
  const lineage = item.lineage
  const status = item.reason.belief.status || 'active'
  if (status === 'confirmed') return 'You confirmed, about'
  if (status === 'refuted') return 'You corrected, about'
  if (status === 'expired') return lineage ? 'Lapsed, about' : 'No longer held, about'
  if (status === 'active') {
    if (lineage === 'again') return 'Formed again, about'
    if (lineage === 'first') return 'New belief about'
    return 'She holds a belief about'
  }
  return 'Belief about'
}

/** A folded line's lead, by the one status every member shares. */
const GROUP_HEAD: Record<string, string> = {
  active: 'Same belief about',
  confirmed: 'You confirmed the same belief about',
  refuted: 'You corrected the same belief about',
  expired: 'Same belief, no longer held, about',
}

const TIMELINE_PAGE = 25

/** A timeline row in the least-sure item shape (plus its `lineage`), so the name, the fold and the drawer opener are shared. */
type LearnedItem = LeastSureItem & { lineage?: TimelineItem['lineage'] }

function asItem(t: TimelineItem): LearnedItem {
  return {
    ...(t.lineage ? { lineage: t.lineage } : {}),
    scope: t.scope,
    id: t.id,
    label: t.label,
    ...(t.labelUnavailable ? { labelUnavailable: t.labelUnavailable } : {}),
    confidence: typeof t.belief.confidence === 'number' ? t.belief.confidence : 0,
    reason: { kind: 'belief', belief: t.belief },
    freshness: t.belief.generatedAt,
    activeBeliefs: 0,
  }
}

/** The day heading: `Fri 23 Oct`, in UTC like every other date on this surface. */
function dayHeading(day: string): string {
  const d = new Date(`${day}T00:00:00.000Z`)
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' })
}

/** The reasons that mean the record is GONE — every other reason is a read that did not work. */
const GONE_REASONS = new Set(['customer_not_found', 'product_card_not_found'])

/**
 * The name for a timeline row — `leastSureName`, except a label the server could not resolve
 * says why. ⚠️ "No longer on record" only for a record that is not there; a failed or
 * throttled read (`label_read_failed`, `account_read_failed`, `workspace_account_unresolved`)
 * is said as one, never as a deleted record.
 */
export function nameOf(item: LeastSureItem): string {
  if (item.label || !item.labelUnavailable || item.labelUnavailable === 'pii_decrypt_unavailable') {
    return leastSureName(item)
  }
  if (GONE_REASONS.has(item.labelUnavailable)) {
    return item.scope === 'customer' ? 'A customer no longer on record' : `Product ${item.id} — not in your catalogue now`
  }
  return item.scope === 'customer'
    ? 'A customer she couldn’t name just now'
    : `Product ${item.id} — she couldn’t name it just now`
}

function sureOf(item: LeastSureItem): { pct: number; text: string; live: boolean } | null {
  const c = item.reason.belief.confidence
  if (typeof c !== 'number') return null
  const pct = Math.round(Math.max(0, Math.min(1, c)) * 100)
  const live = item.reason.belief.status === 'active'
  return { pct, text: live ? `${pct}% sure` : `was ${pct}% sure`, live }
}

export default function LearnedTimeline({
  workspaceId,
  onOpen,
  filter,
  onFilter,
  onRawEvents,
}: {
  workspaceId: string | undefined
  onOpen: (item: LeastSureItem) => void
  /** From the URL (`resolveLearnedFilter`); the page owns it. */
  filter: LearnedFilter
  onFilter: (filter: LearnedFilter) => void
  /** Opens the raw stream (`?view=feed`) — its one entry point. Omitted: no footer. */
  onRawEvents?: () => void
}) {
  const [opened, setOpened] = useState<ReadonlySet<string>>(() => new Set())
  const status = filter === 'all' ? undefined : filter

  const fetchPage = useCallback(
    async(cursor: unknown) => {
      if (!workspaceId) return { items: [] as TimelineItem[], cursor: null }
      const res = await knowledgeApi.getTimeline(workspaceId, {
        status,
        cursor: typeof cursor === 'string' ? cursor : null,
        limit: TIMELINE_PAGE,
      })
      // ⚠️ A response with no `items` is a shape this screen cannot read — never "nothing learned".
      if (!res.success || !res.data || !Array.isArray(res.data.items)) throw new Error('timeline_unavailable')
      // Fail closed on a server that did not apply the filter: its rows are not this list.
      if ((res.data.bound?.status ?? null) !== (status ?? null)) throw new Error('timeline_filter_not_applied')
      return { items: res.data.items, cursor: res.data.cursor ?? null }
    },
    [workspaceId, status]
  )
  const key = workspaceId ? scopeKey('timeline', workspaceId, filter) : null
  const { page, loadMore, retry } = usePagedRead<TimelineItem>(key, fetchPage)

  // Day sections, each folded on exact wording (`foldSameBelief` keys on the UTC day too).
  const days = useMemo(() => {
    const out: { day: string | null; items: LearnedItem[] }[] = []
    for (const t of page?.items ?? []) {
      const item = asItem(t)
      const day = utcDay(t.belief.generatedAt)
      const last = out[out.length - 1]
      if (last && last.day === day) last.items.push(item)
      else out.push({ day, items: [item] })
    }
    return out.map((d) => ({ ...d, entries: foldSameBelief(d.items) }))
  }, [page?.items])

  /** The left gutter: the day on the first row of each day, an empty cell under it. */
  const gutter = (day: string | null, first: boolean) =>
    first ? <span data-testid="kn-learned-day">{day ? dayHeading(day) : 'Undated'}</span> : ''

  const row = (item: LearnedItem, k: string, when: React.ReactNode) => {
    const b = item.reason.belief
    const sure = sureOf(item)
    // An expired row the server could not place may be one she formed again: say so on it.
    const unchecked = b.status === 'expired' && !item.lineage
    return (
      <EntryRow
        key={k}
        when={when}
        testId="kn-learned-row"
        data-status={b.status || 'active'}
        data-scope={item.scope}
        title={
          <>
            <Meta data-testid="kn-learned-head">{headOf(item)}</Meta>{' '}
            <Button variant="link" data-testid="kn-learned-open" onClick={() => onOpen(item)}>
              {nameOf(item)}
            </Button>
          </>
        }
        why={b.hypothesis || 'A belief she holds with no wording recorded.'}
        clamp={false}
        aside={
          sure || unchecked ? (
            <>
              {sure && (
                <span data-testid="kn-learned-sure">
                  <Meter
                    layout="inline"
                    size="sm"
                    label={<span className="mv-sr">How sure</span>}
                    value={sure.pct}
                    valueText={sure.text}
                    muted={!sure.live}
                  />
                </span>
              )}
              {unchecked && (
                <Meta data-testid="kn-learned-unchecked">She couldn’t check whether she formed it again.</Meta>
              )}
            </>
          ) : undefined
        }
      >
        <span data-testid="kn-learned-from">
          <FromLine belief={b} />
        </span>
      </EntryRow>
    )
  }

  let body: React.ReactNode
  if (!workspaceId) body = null
  else if (!page) body = <LoadingState caption="Reading what she learned…" />
  else if (page.failed) {
    body = (
      <ErrorState
        title="She couldn’t read what she learned just now. This is not an empty list — nothing is shown rather than a list that looks complete."
        onRetry={retry}
      />
    )
  } else {
    body = (
      <>
        {page.items.length === 0 ? (
          <EmptyState size="sm">
            {page.hasMore
              ? 'Nothing in the most recent part she read. There is more — load it below.'
              : filter === 'all'
                ? 'She has not formed a belief here yet. Each one appears here as she forms it.'
                : 'Nothing here yet.'}
          </EmptyState>
        ) : (
          <Card padding="sm">
            <EntryList rail label="What she learned, newest first">
              {days.flatMap((d) =>
                d.entries.flatMap((e, i) => {
                  const when = gutter(d.day, i === 0)
                  if (e.kind === 'one') {
                    return [
                      row(
                        e.item,
                        `${e.item.scope}:${e.item.id}:${e.item.reason.belief.generatedAt}:${e.item.reason.belief.hypothesis}`,
                        when
                      ),
                    ]
                  }
                  const ok = JSON.stringify([workspaceId, filter, e.key])
                  const isOpen = opened.has(ok)
                  const noun = e.items[0].scope === 'customer' ? 'customers' : 'products'
                  const from = sharedEvidenceLine(e.items)
                  return [
                    <EntryRow
                      key={e.key}
                      when={when}
                      testId="kn-learned-group"
                      data-count={e.items.length}
                      title={`${GROUP_HEAD[e.items[0].reason.belief.status || 'active'] ?? 'Same belief about'} ${e.items.length} ${noun} · ${e.items.map(nameOf).join(', ')}`}
                      why={e.items[0].reason.belief.hypothesis}
                      clamp={false}
                      aside={
                        <>
                          <Meta>{confidenceRange(e.items)}</Meta>
                          <Button
                            variant="link"
                            aria-expanded={isOpen}
                            data-testid="kn-learned-group-toggle"
                            onClick={() =>
                              setOpened((prev) => {
                                const next = new Set(prev)
                                if (next.has(ok)) next.delete(ok)
                                else next.add(ok)
                                return next
                              })
                            }
                          >
                            {isOpen ? 'Fold them' : 'One by one'}
                          </Button>
                        </>
                      }
                    >
                      {from ? <span data-testid="kn-learned-group-from">From {from}</span> : undefined}
                    </EntryRow>,
                    ...(isOpen
                      ? e.items.map((item) => row(item, `${e.key}:${item.id}:${item.reason.belief.generatedAt}`, ''))
                      : []),
                  ]
                })
              )}
            </EntryList>
          </Card>
        )}
        <LoadMore
          hasMore={page.hasMore}
          onLoad={loadMore}
          label="Load older"
          state={page.loadingMore ? 'loading' : page.moreFailed ? 'error' : 'idle'}
          note={
            page.moreFailed
              ? 'That did not load, so nothing was added. What is above is unaffected — try again.'
              : undefined
          }
          shown={
            page.items.length > 0
              ? page.hasMore
                ? `Showing the ${page.items.length} loaded so far — there are older ones.`
                : `That is everything — ${page.items.length} ${page.items.length === 1 ? 'belief' : 'beliefs'}.`
              : undefined
          }
        />
      </>
    )
  }

  return (
    <Stack data-testid="kn-learned">
      <Stack gap="sm">
        <ChipGroup label="Which beliefs">
          {FILTERS.map((f) => (
            <Chip
              key={f.key}
              kind="select"
              data-learned-filter={f.key}
              selected={filter === f.key}
              onToggle={() => onFilter(f.key)}
            >
              {f.label}
            </Chip>
          ))}
        </ChipGroup>
        <Hint data-testid="kn-learned-note">{FILTER_NOTE[filter]}</Hint>
      </Stack>
      {body}
      {onRawEvents && (
        <Card tone="recessed" padding="sm" data-testid="kn-learned-sources">
          <CardHeader
            titleAs="h2"
            title="Where it comes from"
            description="Store events, sessions, carts and orders from Shopify and your storefront. The raw stream is still there for checking the pipes."
            aside={
              <Button variant="ghost" size="sm" data-testid="kn-raw-events" onClick={onRawEvents}>
                Raw events
              </Button>
            }
          />
        </Card>
      )}
    </Stack>
  )
}
